import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  User, Bell, Trash2, Shield, FileText, Mail, LogOut, Star, Share2,
  ChevronRight, KeyRound, Loader2, AlertTriangle, X, Heart, HelpCircle, Flame
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PRICING, IS_PRELAUNCH } from "@/lib/pricing";
import { Events } from "@/lib/analytics";
import ReserveDealModal from "@/components/ReserveDealModal";
import { shareToWhatsApp } from "@/components/ShareAppCard";

const APP_VERSION = "1.0.0";
const SUPPORT_EMAIL = "hello@sayitbetter.me";

function SettingRow({ icon: Icon, label, sublabel, onClick, right, destructive, testId, interactive = true }) {
  // Render as <div> when `right` is an interactive element (e.g., Switch) to avoid
  // invalid button-in-button HTML nesting.
  const hasInteractiveRight = right !== undefined && right !== null && typeof right === "object";
  const Element = interactive && !hasInteractiveRight ? "button" : "div";

  const commonProps = {
    "data-testid": testId,
    className: `w-full flex items-center gap-3 py-3.5 px-4 ${onClick ? "hover:bg-[#F7F9FB] cursor-pointer" : ""} transition-colors text-left`,
  };
  if (Element === "button") {
    commonProps.onClick = onClick;
    commonProps.type = "button";
  } else if (onClick) {
    commonProps.onClick = onClick;
    commonProps.role = "button";
    commonProps.tabIndex = 0;
    commonProps.onKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    };
  }

  return (
    <Element {...commonProps}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${destructive ? "bg-red-50" : "bg-[#EAF6F4]"}`}>
        <Icon size={18} className={destructive ? "text-[#E07A7A]" : "text-[#4FB3A5]"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${destructive ? "text-[#E07A7A]" : "text-[#1F3A5F]"}`}>{label}</div>
        {sublabel && <div className="text-xs text-[#6B7280] mt-0.5 truncate">{sublabel}</div>}
      </div>
      {right !== undefined ? right : <ChevronRight size={18} className="text-[#A0A7B4]" />}
    </Element>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, updateSettings, deleteAccount, changePassword } = useAuth();
  const [notifOn, setNotifOn] = useState(user?.notifications_enabled ?? true);
  const [showDelete, setShowDelete] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showReserve, setShowReserve] = useState(false);

  const goUpgrade = () => {
    Events.upgradeClick("yearly_5_99", "settings_card");
    if (IS_PRELAUNCH) {
      setShowReserve(true);
    } else {
      navigate("/pricing");
    }
  };

  const toggleNotifs = async (val) => {
    setNotifOn(val);
    const res = await updateSettings({ notifications_enabled: val });
    if (!res.ok) {
      setNotifOn(!val);
      toast.error("Failed to update");
    } else {
      toast.success(val ? "Notifications enabled" : "Notifications disabled");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
    toast.success("Logged out");
  };

  const handleShare = async () => {
    // On settings row, default to opening the system share so users can pick any app.
    // Also surface WhatsApp as a fast path (SMS is one tap away from the Home card).
    shareToWhatsApp("settings_row");
  };

  const handleRate = () => {
    // Google Play link — will be filled in when app is live
    window.open("https://play.google.com/store/apps/details?id=com.sayitbetter.app", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="settings-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-lg mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-[#1F3A5F]">Settings</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Profile card — guest or authed */}
        {isAuthenticated ? (
          <div className="bg-white rounded-2xl p-4 shadow-soft border border-[#E5E7EB]" data-testid="settings-profile-card">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2F5BFF] to-[#4FB3A5] flex items-center justify-center text-white text-xl font-bold">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1F3A5F] truncate">{user?.name || "Welcome"}</div>
                <div className="text-sm text-[#6B7280] truncate">{user?.email}</div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="bg-white rounded-2xl p-5 text-center border border-[#E5E7EB] shadow-soft"
            data-testid="settings-guest-card"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2F5BFF] to-[#4FB3A5] flex items-center justify-center text-white mx-auto mb-3">
              <User size={24} />
            </div>
            <h3 className="text-base font-bold text-[#1F3A5F] mb-1">You're using SayItBetter as a guest</h3>
            <p className="text-sm text-[#6B7280] mb-4 leading-relaxed">
              Create a free account to save your message history and sync across devices.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl border-[#E5E7EB]"
                onClick={() => navigate("/login")}
                data-testid="settings-guest-login"
              >
                Log in
              </Button>
              <Button
                className="flex-1 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white"
                onClick={() => navigate("/signup")}
                data-testid="settings-guest-signup"
              >
                Sign up free
              </Button>
            </div>
          </div>
        )}

        {/* Upgrade to PRO */}
        <div
          className="rounded-2xl overflow-hidden shadow-md cursor-pointer active:scale-[0.99] transition-transform relative"
          onClick={goUpgrade}
          data-testid="settings-upgrade-card"
          style={{
            background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
          }}
        >
          <div
            className="absolute inset-0 opacity-25"
            style={{
              background: "radial-gradient(circle at 85% 20%, rgba(255,255,255,0.6), transparent 45%)",
            }}
          />
          <div className="relative p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                <Flame size={10} />
                {IS_PRELAUNCH ? "PRE-LAUNCH" : "LIMITED TIME"}
              </div>
            </div>
            <h3 className="text-white text-lg font-bold mb-1">
              {IS_PRELAUNCH ? "Reserve your PRO deal" : "Upgrade to PRO"}
            </h3>
            <p className="text-white/80 text-sm leading-snug mb-3">
              {IS_PRELAUNCH
                ? "Free: 3 checks/day · Lock in 1 year of unlimited PRO for $5.99 before we launch on Google Play and the App Store."
                : "Free: 3 checks/day · PRO: Unlimited rewrites, advanced features, and smarter communication tools."}
            </p>
            <p className="text-white text-[13px] font-bold mb-3 flex items-center gap-1.5">
              <Flame size={13} className="text-[#FFD27F]" />
              Limited time: Get 1 year for {PRICING.yearly.price}
              {IS_PRELAUNCH && <span className="text-white/70 text-[11px] font-normal ml-1">(no charge today)</span>}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); goUpgrade(); }}
              data-testid="settings-upgrade-btn"
              className="w-full bg-white text-[#1F3A5F] font-bold py-2.5 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-sm"
            >
              {IS_PRELAUNCH ? "Reserve my deal" : "Upgrade"}
            </button>
          </div>
        </div>

        {/* Account section (authed only) */}
        {isAuthenticated && (
          <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-[#E5E7EB]">
            <div className="px-4 pt-3 pb-1 text-xs font-semibold text-[#A0A7B4] uppercase tracking-wider">Account</div>
            <SettingRow
              icon={KeyRound}
              label="Change password"
              onClick={() => setShowChangePw(true)}
              testId="settings-change-password"
            />
            <SettingRow
              icon={Bell}
              label="Notifications"
              sublabel="Updates about your streaks and tips"
              right={<Switch checked={notifOn} onCheckedChange={toggleNotifs} data-testid="settings-notifications-toggle" />}
              testId="settings-notifications-row"
            />
          </div>
        )}

        {/* Support section */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-[#E5E7EB]">
          <div className="px-4 pt-3 pb-1 text-xs font-semibold text-[#A0A7B4] uppercase tracking-wider">Support</div>
          <SettingRow
            icon={Mail}
            label="Contact support"
            sublabel={SUPPORT_EMAIL}
            onClick={() => (window.location.href = `mailto:${SUPPORT_EMAIL}?subject=SayItBetter%20Support`)}
            testId="settings-contact-support"
          />
          <SettingRow
            icon={HelpCircle}
            label="Help & FAQ"
            onClick={() => navigate("/app/help")}
            testId="settings-help-faq"
          />
        </div>

        {/* About section */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-[#E5E7EB]">
          <div className="px-4 pt-3 pb-1 text-xs font-semibold text-[#A0A7B4] uppercase tracking-wider">About</div>
          <SettingRow
            icon={Star}
            label="Rate the app"
            onClick={handleRate}
            testId="settings-rate-app"
          />
          <SettingRow
            icon={Share2}
            label="Share SayItBetter"
            onClick={handleShare}
            testId="settings-share-app"
          />
          <SettingRow
            icon={Shield}
            label="Privacy Policy"
            onClick={() => navigate("/privacy")}
            testId="settings-privacy"
          />
          <SettingRow
            icon={FileText}
            label="Terms of Service"
            onClick={() => navigate("/terms")}
            testId="settings-terms"
          />
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F3F4F6]">
            <span className="text-sm text-[#6B7280]">App version</span>
            <span className="text-sm text-[#A0A7B4] font-mono">{APP_VERSION}</span>
          </div>
        </div>

        {/* Danger zone (authed only) */}
        {isAuthenticated && (
          <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-[#E5E7EB]">
            <SettingRow
              icon={LogOut}
              label="Log out"
              onClick={handleLogout}
              testId="settings-logout"
              right={<ChevronRight size={18} className="text-[#A0A7B4]" />}
            />
            <SettingRow
              icon={Trash2}
              label="Delete account"
              sublabel="Permanently erase your data"
              destructive
              onClick={() => setShowDelete(true)}
              testId="settings-delete-account"
            />
          </div>
        )}

        <div className="text-center text-xs text-[#A0A7B4] py-4 flex items-center justify-center gap-1.5">
          Made with <Heart size={12} className="text-[#E07A7A] fill-[#E07A7A]" /> for better conversations
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDelete && (
        <DeleteAccountModal
          onClose={() => setShowDelete(false)}
          onConfirm={async (pw) => {
            const res = await deleteAccount(pw);
            if (res.ok) {
              toast.success("Account deleted");
              navigate("/login", { replace: true });
            } else {
              toast.error(res.error || "Failed to delete account");
            }
            return res;
          }}
        />
      )}

      {showChangePw && (
        <ChangePasswordModal
          onClose={() => setShowChangePw(false)}
          onConfirm={async (cur, nw) => {
            const res = await changePassword(cur, nw);
            if (res.ok) {
              toast.success("Password updated");
            } else {
              toast.error(res.error || "Failed to update password");
            }
            return res;
          }}
        />
      )}

      <ReserveDealModal
        isOpen={showReserve}
        onClose={() => setShowReserve(false)}
        source="settings_card"
        defaultEmail={user?.email || ""}
      />
    </div>
  );
}

function DeleteAccountModal({ onClose, onConfirm }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (confirm !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    setLoading(true);
    const res = await onConfirm(password);
    setLoading(false);
    if (!res.ok) return;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="delete-account-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-[#E07A7A]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#1F3A5F]">Delete account?</h3>
            <p className="text-sm text-[#6B7280]">This permanently erases your account and all history. This can't be undone.</p>
          </div>
          <button onClick={onClose} className="text-[#A0A7B4] hover:text-[#1F3A5F]" data-testid="delete-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#6B7280]">Confirm your password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 rounded-xl"
              data-testid="delete-account-password"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#6B7280]">Type DELETE to confirm</label>
            <Input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 rounded-xl"
              data-testid="delete-account-confirm"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl" data-testid="delete-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={loading || confirm !== "DELETE"}
            className="flex-1 rounded-xl bg-[#E07A7A] hover:bg-[#c56969] text-white"
            data-testid="delete-modal-confirm"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Delete forever"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose, onConfirm }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setLoading(true);
    const res = await onConfirm(current, next);
    setLoading(false);
    if (res.ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="change-password-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#EAF6F4] flex items-center justify-center flex-shrink-0">
            <KeyRound size={20} className="text-[#4FB3A5]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#1F3A5F]">Change password</h3>
            <p className="text-sm text-[#6B7280]">Choose a strong password of 8+ characters.</p>
          </div>
          <button onClick={onClose} className="text-[#A0A7B4] hover:text-[#1F3A5F]" data-testid="changepw-close">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#6B7280]">Current password</label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 rounded-xl"
              data-testid="changepw-current"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#6B7280]">New password</label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1 rounded-xl"
              data-testid="changepw-new"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading}
            className="flex-1 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white"
            data-testid="changepw-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
